import React from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Avatar,
  Badge,
  Text,
  Icon,
  IconButton,
  useColorModeValue,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { EditIcon, ViewIcon } from '@chakra-ui/icons';
import { FaProjectDiagram, FaMapMarkerAlt, FaBuilding } from 'react-icons/fa';
import { PhoneIcon } from '@chakra-ui/icons';

const UserListItem = ({ user, handleEditUser, handleRowClick, ...props }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('blue.50', 'gray.700');

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'purple';
      case 'schemeAdmin':
        return 'blue';
      case 'talukaUser':
        return 'green';
      case 'subDivisionalOfficer':
        return 'orange';
      case 'departmentOfficer':
        return 'teal';
      case 'projectOfficer':
        return 'cyan';
      default:
        return 'gray';
    }
  };

  const formatRole = (role) => {
    switch (role) {
      case 'talukaUser':
        return 'Tehsildar';
      case 'schemeAdmin':
        return 'Project Officer (PO)';
      case 'admin':
        return 'Admin';
      case 'subDivisionalOfficer':
        return 'Sub-Divisional Officer (SDO)';
      case 'departmentOfficer':
        return 'Dept. Officer';
      case 'projectOfficer':
        return 'Project Officer (PO)';
      default:
        return role?.charAt(0).toUpperCase() + role?.slice(1) || 'N/A';
    }
  };

  // Helper function to get department names from array
  const getDepartmentNames = (department) => {
    if (!department) return ['N/A'];

    if (Array.isArray(department)) {
      return department.map((dept) =>
        typeof dept === 'object' ? dept.name || 'Department' : 'Department',
      );
    }

    // If it's a single object
    if (typeof department === 'object') {
      return [department.name || 'Department'];
    }

    return ['N/A'];
  };

  // Helper function to get scheme names
  const getSchemeNames = (schemes) => {
    if (!schemes || !Array.isArray(schemes)) return [];

    return schemes.map((scheme) =>
      typeof scheme === 'object' ? scheme.name || 'Scheme' : 'Scheme',
    );
  };

  // Helper function to get taluka names
  const getTalukaNames = (tahsils) => {
    if (!tahsils || !Array.isArray(tahsils)) return [];

    return tahsils.map((taluka) =>
      typeof taluka === 'object' ? taluka.name || 'Taluka' : 'Taluka',
    );
  };

  const departmentNames = getDepartmentNames(user.department);
  const schemeNames = getSchemeNames(user.schemes);
  const talukaNames = getTalukaNames(user.tahsils);

  return (
    <Box
      p={4}
      borderRadius="lg"
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      cursor={handleRowClick ? 'pointer' : 'default'}
      onClick={() => handleRowClick && handleRowClick(user)}
      _hover={{ bg: hoverBg }}
      transition="all 0.2s"
      {...props}
    >
      <Flex align="center" justify="space-between" w="full">
        <HStack spacing={4} flex="1">
          <Avatar name={user.name} size="md" bg="blue.500" />
          <VStack align="start" spacing={2} flex="1">
            <HStack justify="space-between" w="full">
              <Text fontSize="md" fontWeight="semibold" color={textColor}>
                {user.name}
              </Text>
              <Badge
                colorScheme={getRoleColor(user.role)}
                fontSize="xs"
                px={2}
                py={0.5}
                borderRadius="md"
              >
                {formatRole(user.role)}
              </Badge>
            </HStack>

            <Text fontSize="sm" color={subtitleColor}>
              ID: {user.userId}
            </Text>

            {/* Department display with multiple badges */}
            <Box w="full">
              <HStack spacing={1} align="center" mb={1}>
                <Icon as={FaBuilding} color="blue.500" boxSize={3} />
                <Text fontSize="xs" color={subtitleColor}>
                  Departments:
                </Text>
              </HStack>

              <Wrap spacing={1}>
                {departmentNames.slice(0, 2).map((dept, index) => (
                  <WrapItem key={index}>
                    <Badge
                      colorScheme="blue"
                      fontSize="xs"
                      variant="subtle"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                    >
                      {dept}
                    </Badge>
                  </WrapItem>
                ))}

                {departmentNames.length > 2 && (
                  <WrapItem>
                    <Tooltip
                      label={`${departmentNames.slice(2).join(', ')}`}
                      placement="top"
                      hasArrow
                      bg="gray.700"
                      color="white"
                      fontSize="xs"
                      p={2}
                    >
                      <Badge
                        colorScheme="gray"
                        fontSize="xs"
                        variant="subtle"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                        cursor="help"
                      >
                        +{departmentNames.length - 2} more
                      </Badge>
                    </Tooltip>
                  </WrapItem>
                )}
              </Wrap>
            </Box>

            <HStack spacing={3}>
              <Tooltip
                label={user.phoneNo || 'No Phone'}
                placement="top"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="xs"
                p={2}
              >
                <HStack spacing={1} cursor="help">
                  <Icon as={PhoneIcon} color="green.500" boxSize={3} />
                  <Text fontSize="xs" color={subtitleColor}>
                    {user.phoneNo || 'No Phone'}
                  </Text>
                </HStack>
              </Tooltip>

              {schemeNames.length > 0 && (
                <Tooltip
                  label={schemeNames.join(', ')}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="xs"
                  p={2}
                >
                  <HStack spacing={1} cursor="help">
                    <Icon as={FaProjectDiagram} color="green.500" boxSize={3} />
                    <Text fontSize="xs" color={subtitleColor}>
                      {schemeNames.length} Scheme(s)
                    </Text>
                  </HStack>
                </Tooltip>
              )}

              {talukaNames.length > 0 && (
                <Tooltip
                  label={talukaNames.join(', ')}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="xs"
                  p={2}
                >
                  <HStack spacing={1} cursor="help">
                    <Icon as={FaMapMarkerAlt} color="orange.500" boxSize={3} />
                    <Text fontSize="xs" color={subtitleColor}>
                      {talukaNames.length} Taluka(s)
                    </Text>
                  </HStack>
                </Tooltip>
              )}
            </HStack>
          </VStack>
        </HStack>

        <HStack spacing={2}>
          <Badge
            colorScheme={user.active !== false ? 'green' : 'red'}
            fontSize="xs"
            px={2}
            py={0.5}
            borderRadius="md"
          >
            {user.active !== false ? 'Active' : 'Inactive'}
          </Badge>
          <HStack spacing={1}>
            {handleEditUser && (
              <IconButton
                aria-label="Edit User"
                icon={<EditIcon />}
                size="xs"
                colorScheme="blue"
                variant="ghost"
                borderRadius="md"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditUser(user);
                }}
              />
            )}
            {handleRowClick && (
              <IconButton
                aria-label="View Details"
                icon={<ViewIcon />}
                size="xs"
                colorScheme="green"
                variant="ghost"
                borderRadius="md"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowClick(user);
                }}
              />
            )}
          </HStack>
        </HStack>
      </Flex>
    </Box>
  );
};

export default UserListItem;
